import React, { ReactNode, useMemo } from 'react';
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  Customized,
} from 'recharts';
import { Widget } from './Widget';
import { useWidgetZoomControlsPublisher } from './WidgetZoomControlsContext';
import { DataResult, WidgetSettingsDto } from '@thingslog/repositories';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParsed, DataResultParseStream } from './parsing/DataResultParsed';
import {
  ChartEmptyState,
  ChartDataDensityOverlay,
  ChartSeriesConfig,
  ChartTooltipContent,
  ChartWidgetFrame,
  DEFAULT_CHART_COLORS,
  CHART_DATA_POINT_COLOR_KEY,
  applyDataPointColors,
  chartAxisTick,
  chartGridProps,
  getChartLegendProps,
  chartMargin,
  CHART_RESIZE_DEBOUNCE_MS,
  chartTooltipWrapperStyle,
  chartYAxisWidth,
  BAR_MAX_RENDER_POINTS,
  BAR_SCROLL_ZOOM_POINT_THRESHOLD,
  createChartSeries,
  createSmartDateFormatter,
  decimateChartData,
  getVisibleChartData,
  getChartDataPointColor,
  getChartSeriesLegendKeys,
  useChartLegendVisibility,
  useChartViewportControls,
} from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';

interface BarChartContentProps {
  data: { [key: string]: string | number }[];
  series: ChartSeriesConfig[];
  parsedData: DataResultParsed;
  widgetSettings: WidgetSettingsDto;
}

const getBarSizing = (
  dataPointCount: number,
  widgetSettings: WidgetSettingsDto
): { barCategoryGap: number | string; barGap: number; maxBarSize: number; radius: number } => {
  const thicknessMultiplier =
    widgetSettings.type === 'bar-chart' && widgetSettings.visualization.barThickness === 'slim'
      ? 0.7
      : widgetSettings.type === 'bar-chart' && widgetSettings.visualization.barThickness === 'wide'
      ? 1.3
      : 1;

  if (dataPointCount > 120) {
    return {
      barCategoryGap: 0,
      barGap: 0,
      maxBarSize: Math.round(8 * thicknessMultiplier),
      radius: 0,
    };
  }

  if (dataPointCount > 60) {
    return {
      barCategoryGap: 1,
      barGap: 1,
      maxBarSize: Math.round(12 * thicknessMultiplier),
      radius: 1,
    };
  }

  if (dataPointCount > 24) {
    return {
      barCategoryGap: 2,
      barGap: 2,
      maxBarSize: Math.round(20 * thicknessMultiplier),
      radius: 2,
    };
  }

  return {
    barCategoryGap: '12%',
    barGap: 4,
    maxBarSize: Math.round(34 * thicknessMultiplier),
    radius: 4,
  };
};

const BarChartContent = ({
  data,
  series,
  parsedData,
  widgetSettings,
}: BarChartContentProps): JSX.Element => {
  const visualization = widgetSettings.type === 'bar-chart' ? widgetSettings.visualization : null;
  const isHorizontal = visualization?.orientation === 'horizontal';
  const hasViewportControls =
    data.length > BAR_SCROLL_ZOOM_POINT_THRESHOLD && !!parsedData.xAxis?.dataKey;
  const {
    canZoomIn,
    canZoomOut,
    containerClassName,
    containerProps,
    containerStyle,
    isZoomed,
    resetZoom,
    viewport,
    zoomIn,
    zoomOut,
  } = useChartViewportControls({
    axis: isHorizontal ? 'y' : 'x',
    dataLength: data.length,
    enabled: hasViewportControls,
  });

  useWidgetZoomControlsPublisher(
    hasViewportControls ? { canZoomIn, canZoomOut, isZoomed, resetZoom, zoomIn, zoomOut } : null
  );

  const { isSeriesHidden, toggleSeries } = useChartLegendVisibility(
    getChartSeriesLegendKeys(series)
  );

  const visibleData = useMemo(
    () => getVisibleChartData(data, viewport, hasViewportControls),
    [data, hasViewportControls, viewport]
  );
  const renderData = useMemo(
    () => decimateChartData(visibleData, BAR_MAX_RENDER_POINTS, parsedData.xAxis?.dataKey),
    [parsedData.xAxis?.dataKey, visibleData]
  );
  const visibleBarSizing = useMemo(
    () => getBarSizing(renderData.length, widgetSettings),
    [renderData.length, widgetSettings]
  );
  const xAxisFormatter = useMemo(
    () =>
      createSmartDateFormatter(renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter),
    [renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter]
  );

  return (
    <div
      className={`relative h-full w-full ${containerClassName}`}
      style={containerStyle}
      {...containerProps}
    >
      <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
        <BarChart
          data={renderData}
          barCategoryGap={visibleBarSizing.barCategoryGap}
          barGap={visibleBarSizing.barGap}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={chartMargin}
        >
          {visualization?.showGrid !== false && <CartesianGrid {...chartGridProps} />}
          {parsedData.xAxis &&
            parsedData.xAxis.show &&
            visualization?.showXAxisLabels !== false && (
              <XAxis
                dataKey={isHorizontal ? undefined : parsedData.xAxis.dataKey}
                tickFormatter={isHorizontal ? undefined : xAxisFormatter}
                name={parsedData.xAxis.name}
                type={isHorizontal ? 'number' : 'category'}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                dy={8}
                minTickGap={renderData.length > 60 ? 36 : 24}
              />
            )}
          {parsedData.yAxis &&
            parsedData.yAxis.show &&
            visualization?.showYAxisLabels !== false && (
              <YAxis
                dataKey={isHorizontal ? parsedData.xAxis?.dataKey : undefined}
                tickFormatter={isHorizontal ? xAxisFormatter : parsedData.yAxis?.formatter}
                name={parsedData.yAxis?.name}
                type={isHorizontal ? 'category' : 'number'}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                width={isHorizontal ? 92 : chartYAxisWidth}
              />
            )}
          {series.map((item: ChartSeriesConfig) => (
            <Bar
              key={item.dataKey}
              dataKey={item.dataKey}
              fill={item.color}
              name={item.name}
              unit={item.unit}
              stackId={visualization?.grouping === 'stacked' ? '1' : undefined}
              maxBarSize={visibleBarSizing.maxBarSize}
              radius={
                isHorizontal
                  ? [0, visibleBarSizing.radius, visibleBarSizing.radius, 0]
                  : [visibleBarSizing.radius, visibleBarSizing.radius, 0, 0]
              }
              isAnimationActive={false}
              hide={isSeriesHidden(item.dataKey)}
            >
              {item.colorByDataPoint &&
                renderData.map((entry: { [key: string]: string | number }, index: number) => (
                  <Cell
                    key={`${item.dataKey}-${
                      entry[parsedData.xAxis?.dataKey ?? item.dataKey] ?? index
                    }`}
                    fill={
                      typeof entry[CHART_DATA_POINT_COLOR_KEY] === 'string'
                        ? (entry[CHART_DATA_POINT_COLOR_KEY] as string)
                        : getChartDataPointColor(index)
                    }
                  />
                ))}
            </Bar>
          ))}
          <Tooltip
            cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
            wrapperStyle={chartTooltipWrapperStyle}
            content={
              <ChartTooltipContent
                fallbackColors={DEFAULT_CHART_COLORS}
                labelFormatter={(
                  value: string | number,
                  dataPoint?: { [key: string]: string | number }
                ): string =>
                  parsedData.tooltip?.labelFormatter?.(value, dataPoint) ?? String(value)
                }
                markerClassName="rounded-sm"
                valueFormatter={
                  parsedData.tooltip?.formatter
                    ? (value: number | string, name: string): [string, string] =>
                        parsedData.tooltip!.formatter!(value, name, parsedData.dataStreams)
                    : undefined
                }
              />
            }
          />
          {parsedData.legend && parsedData.legend.show && visualization?.showLegend !== false && (
            <Legend
              iconType="square"
              payload={series.map((item: ChartSeriesConfig) => ({
                color: item.color,
                id: item.dataKey,
                inactive: isSeriesHidden(item.dataKey),
                type: 'square' as const,
                value: item.name,
              }))}
              {...getChartLegendProps({
                aside: visualization?.renderLegendAside === true,
                markerClassName: 'rounded-sm',
                onToggleSeries: series.length > 1 ? toggleSeries : undefined,
              })}
            />
          )}
          {hasViewportControls && (
            <Customized
              component={
                <ChartDataDensityOverlay
                  renderedPoints={renderData.length}
                  totalPoints={visibleData.length}
                />
              }
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export class BarChartWidget implements Widget {
  public hasScrollZoomControls(data: DataResult, widgetSettings: WidgetSettingsDto): boolean {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);

    return (
      dataParser.merge(parsedData).length > BAR_SCROLL_ZOOM_POINT_THRESHOLD &&
      !!parsedData.xAxis?.dataKey
    );
  }

  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_bars"
          description="This widget has no bars to display."
        />
      );
    }

    const series = createChartSeries(parsedData.dataStreams, {
      getColor: ({ fill }: { fill?: string }): string | undefined => fill,
    });
    const mergedData = applyDataPointColors(
      dataParser.merge(parsedData),
      series.some((item: ChartSeriesConfig): boolean => !!item.colorByDataPoint)
    );

    return (
      <ChartWidgetFrame>
        <div className="min-h-0 flex-1">
          <BarChartContent
            data={mergedData}
            series={series}
            parsedData={parsedData}
            widgetSettings={normalizedSettings}
          />
        </div>
      </ChartWidgetFrame>
    );
  }
}
